<script setup lang="ts">
/**
 * @fileoverview Development PIN guard to restrict access during development
 *
 * Simple security wall - not production security, just keeps honest people out
 * while the site is under active development. Uses session storage so unlock
 * persists until browser tab is closed.
 *
 * **Production**: Automatically disabled when VITE_ENABLE_PIN_GUARD !== 'true'
 * Set environment variable to enable/disable:
 * - Development: VITE_ENABLE_PIN_GUARD=true (in .env.development)
 * - Production: VITE_ENABLE_PIN_GUARD=false (in .env.production) or omit
 *
 * @example
 * // In App.vue
 * <PinGuard>
 *   <YourContent />
 * </PinGuard>
 */
import { ref, computed, onMounted } from 'vue'
import { useDisplay } from 'vuetify'

// Session storage key for unlock state
const PIN_KEY = 'dev_pin_unlocked'

// Development PIN - change this to your preferred PIN
const CORRECT_PIN = '0711'

// Check if PIN guard is enabled via environment variable
const isEnabled = import.meta.env.VITE_ENABLE_PIN_GUARD === 'true'

const { mobile, xs, sm } = useDisplay()

const pin = ref('')
const error = ref(false)
const unlocked = ref(false)

// Show keypad on mobile/tablet (xs, sm), text input on desktop
const showKeypad = computed(() => mobile.value || xs.value || sm.value)

onMounted(() => {
  // If PIN guard is disabled, automatically unlock
  if (!isEnabled) {
    unlocked.value = true
    return
  }

  // Check if already unlocked in this session
  unlocked.value = sessionStorage.getItem(PIN_KEY) === 'true'
})

/**
 * Add a digit to the PIN (keypad mode)
 * Auto-validates when 4 digits are entered
 */
const addDigit = (digit: string) => {
  if (pin.value.length < 4) {
    pin.value += digit
    error.value = false

    // Auto-check when 4 digits entered
    if (pin.value.length === 4) {
      checkPin()
    }
  }
}

/**
 * Remove last digit from PIN
 */
const backspace = () => {
  pin.value = pin.value.slice(0, -1)
  error.value = false
}

/**
 * Clear entire PIN
 */
const clear = () => {
  pin.value = ''
  error.value = false
}

/**
 * Validate PIN and unlock if correct
 * Stores unlock state in session storage
 */
const checkPin = () => {
  if (pin.value === CORRECT_PIN) {
    // Correct PIN - unlock and save to session
    sessionStorage.setItem(PIN_KEY, 'true')
    unlocked.value = true
    error.value = false
  } else {
    // Incorrect PIN - show error and clear after delay
    error.value = true
    setTimeout(() => {
      pin.value = ''
      error.value = false
    }, 1000)
  }
}

/**
 * Handle form submit in desktop mode (text input)
 */
const handleDesktopSubmit = () => {
  if (pin.value.length === 4) {
    checkPin()
  }
}
</script>

<template>
  <!-- PIN Guard Overlay - Only shows when locked and enabled -->
  <div v-if="!unlocked" class="pin-guard">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="4" xl="3">
          <v-card class="pa-6" elevation="8">
            <v-card-title class="text-h5 text-center mb-4">
              🔒 Development Access
            </v-card-title>

            <v-card-text>
              <p class="text-center text-body-2 text-medium-emphasis mb-6">
                Enter PIN to access site
              </p>

              <!-- Desktop Mode: Text Input Field -->
              <div v-if="!showKeypad" class="text-center mb-4">
                <v-text-field
                  v-model="pin"
                  type="password"
                  maxlength="4"
                  variant="outlined"
                  density="comfortable"
                  placeholder="Enter 4-digit PIN"
                  :error="error"
                  :error-messages="error ? 'Incorrect PIN' : ''"
                  autofocus
                  class="pin-input"
                  aria-label="PIN entry field"
                  @keyup.enter="handleDesktopSubmit"
                />
                <v-btn
                  color="primary"
                  size="large"
                  block
                  :disabled="pin.length !== 4"
                  aria-label="Unlock access"
                  @click="handleDesktopSubmit"
                >
                  Unlock
                </v-btn>
              </div>

              <!-- Mobile/Tablet Mode: Visual PIN Display + Keypad -->
              <div v-else>
                <!-- PIN Dots Display (shows filled dots as user enters digits) -->
                <div class="pin-display mb-6">
                  <div class="pin-dots">
                    <v-icon
                      v-for="i in 4"
                      :key="i"
                      :icon="i <= pin.length ? 'mdi-circle' : 'mdi-circle-outline'"
                      :color="error ? 'error' : i <= pin.length ? 'primary' : 'grey-lighten-1'"
                      size="24"
                    />
                  </div>
                  <div v-if="error" class="text-error text-center mt-2">
                    Incorrect PIN
                  </div>
                </div>

                <!-- Phone-Style Keypad (3x4 grid: 1-9, Clear/0/Backspace) -->
                <div class="keypad" role="group" aria-label="PIN keypad">
                  <v-row dense>
                    <v-col v-for="num in [1, 2, 3]" :key="num" cols="4">
                      <v-btn
                        variant="outlined"
                        size="x-large"
                        block
                        class="keypad-btn"
                        :aria-label="`Digit ${num}`"
                        @click="addDigit(num.toString())"
                      >
                        {{ num }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col v-for="num in [4, 5, 6]" :key="num" cols="4">
                      <v-btn
                        variant="outlined"
                        size="x-large"
                        block
                        class="keypad-btn"
                        :aria-label="`Digit ${num}`"
                        @click="addDigit(num.toString())"
                      >
                        {{ num }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col v-for="num in [7, 8, 9]" :key="num" cols="4">
                      <v-btn
                        variant="outlined"
                        size="x-large"
                        block
                        class="keypad-btn"
                        :aria-label="`Digit ${num}`"
                        @click="addDigit(num.toString())"
                      >
                        {{ num }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="4">
                      <v-btn
                        variant="outlined"
                        size="x-large"
                        block
                        class="keypad-btn"
                        aria-label="Clear PIN"
                        @click="clear"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-col>
                    <v-col cols="4">
                      <v-btn
                        variant="outlined"
                        size="x-large"
                        block
                        class="keypad-btn"
                        aria-label="Digit 0"
                        @click="addDigit('0')"
                      >
                        0
                      </v-btn>
                    </v-col>
                    <v-col cols="4">
                      <v-btn
                        variant="outlined"
                        size="x-large"
                        block
                        class="keypad-btn"
                        aria-label="Backspace"
                        @click="backspace"
                      >
                        <v-icon>mdi-backspace-outline</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <p class="text-caption text-center text-disabled mt-4">
                Development environment only
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>

  <!-- Show app content when unlocked -->
  <slot v-else />
</template>

<style scoped>
.pin-guard {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-secondary)) 100%);
  z-index: 9999;
}

.pin-display {
  background: rgb(var(--v-theme-on-surface) / 0.02);
  border-radius: 12px;
  padding: 24px;
}

.pin-dots {
  display: flex;
  justify-content: center;
  gap: 16px;
}

.keypad {
  max-width: 320px;
  margin: 0 auto;
}

.keypad-btn {
  height: 64px !important;
  font-size: 24px;
  font-weight: 500;
}

.pin-input {
  max-width: 300px;
  margin: 0 auto;
}

/* Smoother transitions */
.keypad-btn {
  transition: transform 0.1s ease;
}

.keypad-btn:active {
  transform: scale(0.95);
}
</style>
